/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useClientTouchDetection } from "../hooks/useClientTouchDetection";

interface EventState {
  eventName: string | null;
  eventData?: any;
}

interface MobileContextType {
  isMobile: boolean;
  isTouchDevice: boolean;
  event: EventState;
  triggerEvent: (eventName: string, eventData?: any) => void;
}

const MobileContext = createContext<MobileContextType | undefined>(undefined);

interface MobileContextProviderProps {
  isMobile: boolean;
  isTouchDevice: boolean; // Server-side fallback, will be overridden by client-side detection
  children: React.ReactNode;
}

export function MobileContextProvider({
  isMobile,
  isTouchDevice: serverTouchDevice,
  children,
}: MobileContextProviderProps) {
  const [event, setEvent] = useState<EventState>({ eventName: null });
  const [isHydrated, setIsHydrated] = useState(false);

  // Use client-side detection, fallback to server-side during SSR
  const clientTouchDevice = useClientTouchDetection();

  // Track when hydration is complete
  React.useEffect(() => {
    setIsHydrated(true);
  }, []);

  // During SSR or before hydration, use server value, then switch to client detection
  const isTouchDevice =
    typeof window === "undefined" || !isHydrated
      ? serverTouchDevice
      : clientTouchDevice;

  const triggerEvent = useCallback((eventName: string, eventData?: any) => {
    setEvent({ eventName, eventData });
  }, []);

  const value = useMemo(
    () => ({ isMobile, isTouchDevice, event, triggerEvent }),
    [isMobile, isTouchDevice, event, triggerEvent]
  );

  return (
    <MobileContext.Provider value={value}>{children}</MobileContext.Provider>
  );
}

export function useMobileContext(): MobileContextType {
  const context = useContext(MobileContext);
  if (!context) {
    throw new Error(
      "useMobileContext must be used within a MobileContextProvider"
    );
  }
  return context;
}

/** Like useMobileContext, but null outside the provider (routes rendered without the shell). */
export function useOptionalMobileContext(): MobileContextType | null {
  return useContext(MobileContext) ?? null;
}
